import { ThemedStyledProps } from "../styled"
import { Breakpoint } from "./breakpoints"
import { Theme } from "../theme"
import { up } from "./mediaQueries"
import { inverseProps } from "../utils"
import { curry, has, is, filter, pipe } from "ramda"

type TLength = string | number
export type ResponsiveProp<T = TLength> = { xs: T } & {
  [K in Exclude<Breakpoint, "xs">]?: T
}
export type Responsive<T = TLength> = T | ResponsiveProp<T>
export type ResponsiveProps<P, K extends keyof P = keyof P> = {
  [key in K]: P[K] | ({ xs: P[K] } & { [key in Breakpoint]?: P[K] })
}

const isResponsive = (prop: any) => is(Object, prop) && has("xs", prop)

const toResponsive = <P>(theme: Theme, props: P) =>
  Object.entries(props).reduce(
    (acc, [k, v]) => ({
      ...acc,
      [up(k as Breakpoint, { theme })]: v
    }),
    {}
  )

const toResponsiveProp = <P>(key: keyof P, props: P) =>
  Object.entries(props[key]).reduce(
    (acc, [k, v]) => ({
      ...acc,
      [up(k as Breakpoint, (props as any) as { theme: Theme })]: { [key]: v }
    }),
    {}
  )

const responsiveProp = curry(<P>(key: keyof P, props: P) => {
  switch (true) {
    case props[key] === undefined:
      return {}
    case isResponsive(props[key]):
      return toResponsiveProp(key, props as ThemedStyledProps<P>)
    default:
      return {
        [key]: props[key]
      }
  }
})

const responsiveProps = pipe(filter(isResponsive), inverseProps)

export {
  isResponsive,
  responsiveProp,
  responsiveProps,
  toResponsive,
  toResponsiveProp
}
